/**
 * Miller Harris — GWP Gift Picker
 *
 * When a GWP rule has multiple gift options (giftOptions array in the rule),
 * this script renders a visual picker in the cart page matching the free
 * samples picker style. Customer selects one gift, it's added to the cart,
 * and the Product Discount Function zeros it at checkout.
 *
 * Reads rules from #mh-gwp-rules JSON (rendered by mh-gwp-rules.liquid).
 * Only activates when a rule has giftOptions AND the customer is eligible.
 */

(function () {
  'use strict';

  var GWP_CART_PROPERTY = '_gwp_rule';

  function getOrCreatePickerElements() {
    var pickerEl = document.querySelector('[data-gwp-picker]');
    if (!pickerEl) {
      // Picker was removed from DOM (cart section re-rendered). Re-create it.
      var cartItems = document.querySelector('[data-cart-page] [data-cart-item]');
      var insertAfter = cartItems ? cartItems.parentElement : null;
      if (!insertAfter) return null;

      pickerEl = document.createElement('div');
      pickerEl.id = 'cart-gwp-picker';
      pickerEl.className = 'mt-4 md:mt-6 samples-cross-sell';
      pickerEl.setAttribute('data-gwp-picker', '');
      pickerEl.style.display = 'none';
      pickerEl.innerHTML =
        '<div class="flex justify-between items-baseline">' +
          '<p class="samples-cross-sell__title" data-gwp-picker-title>Choose your free gift</p>' +
        '</div>' +
        '<div class="samples-cross-sell__product-list" data-gwp-picker-list></div>' +
        '<p class="text-xs mt-1" data-gwp-picker-counter></p>';

      // Insert after the last cart item
      insertAfter.parentNode.insertBefore(pickerEl, insertAfter.nextSibling);
    }
    return {
      picker: pickerEl,
      list: pickerEl.querySelector('[data-gwp-picker-list]'),
      counter: pickerEl.querySelector('[data-gwp-picker-counter]'),
      title: pickerEl.querySelector('[data-gwp-picker-title]')
    };
  }

  var els = getOrCreatePickerElements();
  if (!els) return;
  var pickerEl = els.picker;
  var listEl = els.list;
  var counterEl = els.counter;
  var titleEl = els.title;

  var rules = [];
  try {
    rules = JSON.parse(document.getElementById('mh-gwp-rules')?.textContent || '[]');
  } catch (e) { return; }

  // Find rules with self-select gift options
  var selfSelectRules = rules.filter(function (r) {
    return r.giftOptions && r.giftOptions.length > 1;
  });

  if (selfSelectRules.length === 0) return;

  var processing = false;

  // Safety: reset processing flag if stuck for more than 10 seconds
  setInterval(function () { processing = false; }, 10000);

  // ── Cart API helpers ──────────────────────────────────────────────────

  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function addToCart(variantId, properties) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: properties || {} }]
      })
    }).then(function (r) { return r.json(); });
  }

  function removeFromCart(lineKey) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    }).then(function (r) { return r.json(); });
  }

  // ── Eligibility (matches mh-gwp-manager.js logic) ─────────────────────

  function isEligible(rule, cart) {
    // Code-based rules: code must be applied AND non-code conditions must be met
    if (rule.discountCode) {
      try {
        var codes = JSON.parse(sessionStorage.getItem('_mh_gwp_codes') || '[]');
        if (!codes.includes(rule.discountCode.toUpperCase())) return false;
        // Code is applied — fall through to check condition type
      } catch { return false; }
    }

    switch (rule.conditionType) {
      case 'minimum_spend':
        var subtotal = cart.total_price / 100;
        var giftTotal = cart.items.reduce(function (sum, item) {
          if (item.properties && item.properties[GWP_CART_PROPERTY]) {
            return sum + (item.final_line_price / 100);
          }
          return sum;
        }, 0);
        return (subtotal - giftTotal) >= rule.spendThreshold;

      case 'specific_product':
        if (!rule.qualifyingProductId) return false;
        return cart.items.some(function (item) {
          if (item.properties && item.properties[GWP_CART_PROPERTY]) return false;
          if (rule.qualifyingVariantId) return item.variant_id === rule.qualifyingVariantId;
          return item.product_id === rule.qualifyingProductId;
        });

      case 'none':
        return true;

      default:
        return false;
    }
  }

  // ── Find which gift (if any) from this rule is in the cart ────────────

  function findGiftInCart(cart, rule) {
    for (var i = 0; i < rule.giftOptions.length; i++) {
      var opt = rule.giftOptions[i];
      var found = cart.items.find(function (item) {
        return item.variant_id === opt.variantId &&
          item.properties &&
          item.properties[GWP_CART_PROPERTY] === rule.name;
      });
      if (found) return { option: opt, lineItem: found };
    }
    return null;
  }

  // ── Check variant availability (cached for the session) ─────────────

  var availabilityCache = {};

  function checkAvailability(variantId) {
    if (availabilityCache[variantId] !== undefined) {
      return Promise.resolve(availabilityCache[variantId]);
    }
    return fetch('/variants/' + variantId + '.js', {
      headers: { 'Accept': 'application/json' }
    }).then(function (r) {
      if (!r.ok) return false;
      return r.json().then(function (v) {
        availabilityCache[variantId] = v.available;
        return v.available;
      });
    }).catch(function () { return false; });
  }

  // ── Render the picker ─────────────────────────────────────────────────

  async function renderPicker(rule, cart) {
    var giftInCart = findGiftInCart(cart, rule);

    // Show picker immediately to avoid any flash of invisibility
    pickerEl.style.display = '';

    if (titleEl && rule.message) {
      titleEl.textContent = 'Choose your free gift';
    }

    // Use cached availability if we have it — render immediately.
    // Otherwise show all options and silently update once we know.
    var availableOptions = rule.giftOptions.filter(function (opt) {
      return availabilityCache[opt.variantId] !== false; // unknown or true
    });

    // Kick off availability checks in background (cache results for next render)
    Promise.all(rule.giftOptions.map(function (opt) {
      return checkAvailability(opt.variantId);
    })).then(function () {
      // If any availability result changed, re-render (silently, cache is warm now)
      var newAvailable = rule.giftOptions.filter(function (opt) {
        return availabilityCache[opt.variantId] !== false;
      });
      if (newAvailable.length !== availableOptions.length) {
        renderPicker(rule, cart);
      }
    });

    // Only hide if ALL options are known-unavailable (not during first load)
    if (availableOptions.length === 0) {
      pickerEl.style.display = 'none';
      return;
    }

    listEl.innerHTML = availableOptions.map(function (opt) {
      var isSelected = giftInCart && giftInCart.option.variantId === opt.variantId;
      var escTitle = opt.title.replace(/"/g, '&quot;').replace(/</g, '&lt;');
      var imageHtml = opt.image
        ? '<div class="samples-cross-sell__product__image-container">'
          + '<img src="' + opt.image + '&width=300" alt="' + escTitle + '" loading="lazy" class="w-full h-auto object-contain samples-cross-sell__product__image">'
          + '</div>'
        : '';

      var buttonText = isSelected ? 'Added' : 'Add - Free';
      var buttonClass = 'samples-cross-sell__product__button btn-transparent-black';
      var selectedAttr = isSelected ? 'data-selected="true"' : 'data-selected="false"';
      var disabledAttr = giftInCart ? ' disabled style="opacity:0.4;cursor:not-allowed;pointer-events:none;"' : '';

      return '<div class="samples-cross-sell__product" data-gwp-gift data-variant-id="' + opt.variantId + '">'
        + '<a href="#" class="block" onclick="return false;">' + imageHtml + '</a>'
        + '<div class="flex flex-col justify-between flex-1">'
        + '<div class="">'
        + '<span class="samples-cross-sell__product__title">' + escTitle + '</span>'
        + (opt.variantTitle && opt.variantTitle !== 'Default Title'
          ? '<span class="samples-cross-sell__product__variant-title">' + opt.variantTitle + '</span>'
          : '')
        + '</div>'
        + '<button type="button" class="' + buttonClass + '" '
        + 'data-gwp-add-gift data-variant-id="' + opt.variantId + '" data-rule-name="' + rule.name.replace(/"/g, '&quot;') + '" '
        + selectedAttr + disabledAttr + '>'
        + buttonText
        + '</button>'
        + '</div>'
        + '</div>';
    }).join('');

    // Counter
    if (counterEl) {
      counterEl.textContent = 'Free gift selected: ' + (giftInCart ? '1' : '0') + '/1';
    }

    pickerEl.style.display = '';
  }

  // ── Handle gift selection ─────────────────────────────────────────────

  function handleGiftClick(e) {
    var btn = e.target.closest('[data-gwp-add-gift]');
    if (!btn || btn.disabled) return;

    // Capture values immediately — btn reference may become stale after DOM changes
    var variantId = parseInt(btn.getAttribute('data-variant-id'));
    var ruleName = btn.getAttribute('data-rule-name');
    if (!variantId || !ruleName) return;

    // Disable all gift buttons to prevent double-clicks
    document.querySelectorAll('[data-gwp-add-gift]').forEach(function (b) { b.disabled = true; });
    btn.textContent = 'Adding...';

    // Use async IIFE for cleaner flow
    (async function () {
      try {
        var cart = await getCart();
        var rule = selfSelectRules.find(function (r) { return r.name === ruleName; });
        if (!rule) return;

        // Remove any existing gift from this rule first
        var existing = findGiftInCart(cart, rule);
        if (existing) {
          await removeFromCart(existing.lineItem.key);
        }

        // If clicking the already-selected gift, just remove it (toggle off)
        if (!(existing && existing.option.variantId === variantId)) {
          // Add the new gift
          var props = {};
          props[GWP_CART_PROPERTY] = ruleName;
          await addToCart(variantId, props);
        }

        // Update picker in-place and refresh cart section without full page reload
        cart = await getCart();
        if (!document.contains(pickerEl)) { refreshElements(); }
        await renderPicker(rule, cart);

        // Refresh the cart items section via Shopify section rendering
        try {
          var sectionId = document.querySelector('[data-section]')?.dataset?.section || 'cart--main';
          var sectionRes = await fetch('/cart?sections=' + sectionId + ',main-cart,cart');
          if (sectionRes.ok) {
            var sections = await sectionRes.json();
            var sectionHtml = sections[sectionId] || sections['main-cart'] || sections['cart'];
            if (sectionHtml) {
              var parser = new DOMParser();
              var doc = parser.parseFromString(sectionHtml, 'text/html');
              var newGrid = doc.querySelector('.cart-grid');
              var oldGrid = document.querySelector('.cart-grid');
              if (newGrid && oldGrid) {
                oldGrid.innerHTML = newGrid.innerHTML;
              }
            }
          }
        } catch (e) { /* silent - picker is already updated */ }

        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('cart:quantity-updated'));
      } catch (err) {
        console.error('[MH GWP Picker] Error adding gift:', err);
        // Re-enable buttons on error
        document.querySelectorAll('[data-gwp-add-gift]').forEach(function (b) { b.disabled = false; });
      }
    })();
  }

  // Delegate click events to document — survives DOM re-renders
  document.addEventListener('click', handleGiftClick);

  // ── Check and render on load / cart changes ───────────────────────────

  var currentlyShownRule = null;

  function refreshElements() {
    var els = getOrCreatePickerElements();
    if (!els) return false;
    pickerEl = els.picker;
    listEl = els.list;
    counterEl = els.counter;
    titleEl = els.title;
    return true;
  }

  // Cache the last cart + shown rule so re-renders can show the picker
  // instantly without waiting for an async cart fetch.
  var lastCart = null;

  function renderFromCart(cart) {
    for (var i = 0; i < selfSelectRules.length; i++) {
      var rule = selfSelectRules[i];
      var giftFromRuleInCart = rule.giftOptions.some(function (opt) {
        return cart.items.some(function (item) {
          return item.variant_id === opt.variantId &&
            item.properties && item.properties[GWP_CART_PROPERTY] === rule.name;
        });
      });
      if (isEligible(rule, cart) || giftFromRuleInCart) {
        renderPicker(rule, cart);
        currentlyShownRule = rule.name;
        return true;
      }
    }
    currentlyShownRule = null;
    pickerEl.style.display = 'none';
    return false;
  }

  function checkAndRender() {
    // Ensure elements exist (cart section may have re-rendered)
    if (!document.contains(pickerEl)) {
      if (!refreshElements()) return;
      // If we have a cached cart, render immediately to avoid flicker
      if (lastCart) {
        renderFromCart(lastCart);
      }
    }

    // Always fetch fresh cart in the background and re-render
    getCart().then(function (cart) {
      lastCart = cart;
      if (!document.contains(pickerEl)) {
        if (!refreshElements()) return;
      }
      renderFromCart(cart);
    });
  }

  // Initial check
  checkAndRender();

  // Re-check on cart updates — debounced to avoid rapid re-checks
  // from free samples or other scripts
  var renderTimer = null;
  function debouncedCheckAndRender() {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(checkAndRender, 800);
  }
  document.addEventListener('cart:updated', debouncedCheckAndRender);
  document.addEventListener('cart:quantity-updated', debouncedCheckAndRender);

  // Watch for cart section re-renders — immediately restore picker from cache
  // so it never disappears, then refresh from the API in the background.
  var restoreObserver = new MutationObserver(function () {
    if (!document.contains(pickerEl)) {
      if (refreshElements() && lastCart) {
        renderFromCart(lastCart);
      }
    }
  });
  var cartPage = document.querySelector('[data-cart-page]') || document.body;
  restoreObserver.observe(cartPage, { childList: true, subtree: true });
})();
